const { UnauthorizedError } = require('@branchjs/tools.errors');
const isSANAdPartnerWithNoCampaignIdData = require('./isSANAdPartnerWithNoCampaignIdData');

const vendorHelpers = {};

const AGENCY_TAGGED_DATA = 'agency_tagged_data';
const AD_NETWORKS = 'ad_networks';
const LOCATION = 'location';
const REVENUE_DATA = 'revenue_data';
const SK_ADNETWORK_SETTINGS = 'sk_ad_network_settings';
const CAMPAIGN_TO_AGENCY_MAPPING = 'campaign_to_agency_mapping';
const AD_PARTNER_TO_CAMPAIGN_ID_MAPPING = 'ad_partner_to_campaign_id_mapping';

const AGENCY_TAGGED_DATA_FULL_ACCESS = 1;
const AD_NETWORKS_FULL_ACCESS = 1;
const LOCATION_FULL_ACCESS = 1;
const REVENUE_DATA_FULL_ACCESS = 1;

const REQUESTING_DATA_FOR_AD_NETWORKS_ERROR = 'Error Requesting Analytics Data For Ad Networks';

vendorHelpers.ERROR_CONVERTING_AD_NETWORK_NAMES_TO_COMPANY_NAMES = 'Error Converting Adnetwork Names To Company Names';

vendorHelpers.ACCESS_TO_REVENUE_DATA_RESTRICTED = 'Access To Revenue Data Restricted';

vendorHelpers.AGENCY_TAGGED_DATA_FIELD = 'last_attributed_touch_data.~agency_id';

vendorHelpers.LOCATION_FIELD = 'user_data.geo_country_code';

vendorHelpers.AD_NETWORKS_FIELD = 'last_attributed_touch_data.$3p';

vendorHelpers.AGENCY_TAGGED_DATA = AGENCY_TAGGED_DATA;

vendorHelpers.AD_NETWORKS = AD_NETWORKS;

vendorHelpers.LOCATION = LOCATION;

vendorHelpers.REVENUE_DATA = REVENUE_DATA;

vendorHelpers.protectedLuceneFields = [AGENCY_TAGGED_DATA, LOCATION, AD_NETWORKS];

vendorHelpers.protectedLuceneFieldsToPermissionKeys = {
  [vendorHelpers.AGENCY_TAGGED_DATA_FIELD]: AGENCY_TAGGED_DATA,
  [vendorHelpers.LOCATION_FIELD]: LOCATION,
  [vendorHelpers.AD_NETWORKS_FIELD]: AD_NETWORKS,
};

vendorHelpers.permissionKeysToProtectedLuceneFields = {
  [AGENCY_TAGGED_DATA]: vendorHelpers.AGENCY_TAGGED_DATA_FIELD,
  [LOCATION]: vendorHelpers.LOCATION_FIELD,
  [AD_NETWORKS]: vendorHelpers.AD_NETWORKS_FIELD,
};

vendorHelpers.VENDOR_TYPES = {
  COHORT: 'COHORT',
  SK_AD_NETWORK: 'SK_AD_NETWORK',
};

vendorHelpers.VENDOR_DATASOURCES = {
  ARTIFICIAL: {
    FILTERS_REQUEST: 'FILTERS_REQUEST',
    INSTALL_COHORT: 'install_cohort',
    REENGAGEMENT_COHORT: 'reengagement_cohort',
    SK_AD_NETWORK_FILTERS_REQUEST: 'SK_AD_NETWORK_FILTERS_REQUEST',
  },
  SK_AD_NETWORK_VALID_MESSAGES: 'skadnetwork-valid-messages',
};

const isCohort = (options) => {
  return (
    options.datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.INSTALL_COHORT ||
    options.filter.install_activity_touch_data_tilde_advertising_partner_name ||
    options.filter.install_activity_touch_data_dollar_3p ||
    options.datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.REENGAGEMENT_COHORT ||
    options.filter.reengagement_activity_touch_data_tilde_advertising_partner_name ||
    options.filter.reengagement_activity_touch_data_dollar_3p
  );
};

const isCohortFilterSearch = (query) => {
  if (query && query.dataSource && query.queryType) {
    return ['install_cohort', 'reengagement_cohort'].includes(query.dataSource) && query.queryType === 'search';
  }
};

const isSKAdNetwork = (options) => {
  return (
    options.datasource === vendorHelpers.VENDOR_DATASOURCES.SK_AD_NETWORK_VALID_MESSAGES ||
    options.datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.SK_AD_NETWORK_FILTERS_REQUEST
  );
};
vendorHelpers.isSKAdNetwork = isSKAdNetwork;

vendorHelpers.isTypeOf = (vendorType, options) => {
  if (!vendorType || !options) {
    return false;
  }

  if (vendorType === vendorHelpers.VENDOR_TYPES.COHORT && options.filter && options.datasource) {
    return isCohort(options);
  }
  if (vendorType === vendorHelpers.VENDOR_TYPES.SK_AD_NETWORK && options.datasource) {
    return isSKAdNetwork(options);
  }

  return false;
};

vendorHelpers.generateORFilter = (accessFilter) => {
  const filterFinal = accessFilter.map((value) => `\"${value}\"`);
  return `(${filterFinal.join(' OR ')})`;
};

vendorHelpers.generateFilter = (accessFilter, isPostback) => {
  if (accessFilter.length === 1) {
    // The ad-network-service automatically adds quotes around terms so skip for postbacks
    return isPostback ? `${accessFilter}` : `"${accessFilter}"`;
  }
  return vendorHelpers.generateORFilter(accessFilter);
};

vendorHelpers.getPermittedAdNetworksAsCompanyNames = function (machineNames, adNetworks) {
  if (!adNetworks || typeof adNetworks !== 'object' || Object.keys(adNetworks).length === 0) {
    throw new Error(vendorHelpers.ERROR_CONVERTING_AD_NETWORK_NAMES_TO_COMPANY_NAMES);
  }

  return machineNames.reduce((matches, machineName) => {
    const companyName = adNetworks[machineName];
    if (companyName) {
      matches.push(adNetworks[machineName]);
    }
    return matches;
  }, []);
};

/**
 * Function that gets the adnetwork filter for agencies based on the cohort type and whether any permitted ad partners exist.
 * The reason we use both machine names (*_activity_touch_data_dollar_3p) and ad partner names (*_activity_touch_data_tilde_advertising_partner_name)
 * is because before 9/19, cohorts topics did not have *_activity_touch_data_dollar_3p as a dimension so we therefore had to rely on ad partner name.
 * However, using only ad partner name is insufficient since we procure this list of ad partners from the ad partner blueprint, which does not
 * contain custom ad partner. Therefore, to account for (a) the missing dollar 3p dimension before 9/19 and (b) custom ad partner, we must use
 * an OR filter when checking whether an agency user has access to specific ad networks.
 */
vendorHelpers.getAdNetworkFiltersForCohorts = (
  permittedMachineNames,
  permittedAdPartners,
  datasource,
  filter = {},
  isCohortFilterSearch
) => {
  let machineNameDimension;
  let adPartnerDimension;

  if (
    datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.INSTALL_COHORT ||
    filter.install_activity_touch_data_tilde_advertising_partner_name ||
    filter.install_activity_touch_data_dollar_3p
  ) {
    machineNameDimension = vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_TOUCH_DATA_DOLLAR_3p;
    adPartnerDimension = vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_TOUCH_DATA_TILDE_ADVERTISING_PARTNER_NAME;
  } else {
    machineNameDimension = vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_TOUCH_DATA_DOLLAR_3p;
    adPartnerDimension = vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_TOUCH_DATA_TILDE_ADVERTISING_PARTNER_NAME;
  }

  if (isCohortFilterSearch) {
    return {
      type: 'or',
      fields: [
        {
          type: 'in',
          dimension: vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_TOUCH_DATA_DOLLAR_3p,
          values: permittedMachineNames,
        },
        {
          type: 'in',
          dimension: vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_TOUCH_DATA_DOLLAR_3p,
          values: permittedMachineNames,
        },
      ],
    };
  }

  if (!permittedAdPartners.length) {
    return {
      type: 'in',
      dimension: machineNameDimension,
      values: permittedMachineNames,
    };
  }

  return {
    type: 'or',
    fields: [
      {
        type: 'in',
        dimension: adPartnerDimension,
        values: permittedAdPartners,
      },
      {
        type: 'in',
        dimension: machineNameDimension,
        values: permittedMachineNames,
      },
    ],
  };
};

vendorHelpers.limitDataToVendor = function (agencyUser, permissionToResources, permissionToLookUp, maxPermissionLevel) {
  return agencyUser && permissionToResources[permissionToLookUp].permission < maxPermissionLevel;
};

vendorHelpers.hasDegradedPermissions = function (options) {
  const { isAgencyUser, permissions } = options;
  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, AGENCY_TAGGED_DATA, AGENCY_TAGGED_DATA_FULL_ACCESS)) {
    return true;
  }
  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, LOCATION, LOCATION_FULL_ACCESS)) {
    return true;
  }
  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, AD_NETWORKS, AD_NETWORKS_FULL_ACCESS)) {
    return true;
  }
  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, REVENUE_DATA, REVENUE_DATA_FULL_ACCESS)) {
    return true;
  }
  return false;
};

vendorHelpers.getFiltersForDegradedPermissions = function (
  permissions,
  isAgencyUser,
  vendorId,
  whitelistedCampaignIdAdPartnerMap
) {
  const degradedPermissionsAccessFilters = {};
  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, AGENCY_TAGGED_DATA, AGENCY_TAGGED_DATA_FULL_ACCESS)) {
    degradedPermissionsAccessFilters[AGENCY_TAGGED_DATA] = [vendorId];
  }
  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, LOCATION, LOCATION_FULL_ACCESS)) {
    degradedPermissionsAccessFilters[LOCATION] = vendorHelpers.extractAccessFilter(permissions, LOCATION);
  }
  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, AD_NETWORKS, AD_NETWORKS_FULL_ACCESS)) {
    degradedPermissionsAccessFilters[AD_NETWORKS] = vendorHelpers.extractAccessFilter(permissions, AD_NETWORKS);
  }

  if (vendorHelpers.limitDataToVendor(isAgencyUser, permissions, REVENUE_DATA, REVENUE_DATA_FULL_ACCESS)) {
    degradedPermissionsAccessFilters[REVENUE_DATA] = true;
  }

  if (whitelistedCampaignIdAdPartnerMap && Object.keys(whitelistedCampaignIdAdPartnerMap).length) {
    degradedPermissionsAccessFilters[AD_PARTNER_TO_CAMPAIGN_ID_MAPPING] = whitelistedCampaignIdAdPartnerMap;
  }

  return degradedPermissionsAccessFilters;
};

vendorHelpers.extractAccessFilter = function (permissionToResources, permissionToLookUp) {
  return permissionToResources[permissionToLookUp].access_filter;
};

vendorHelpers.filterSKANSettingsForVendor = function (sKAdNetworkSettings, vendorId) {
  if (!sKAdNetworkSettings || !sKAdNetworkSettings[CAMPAIGN_TO_AGENCY_MAPPING]) {
    return sKAdNetworkSettings;
  }

  const filteredCampaignToAgencyMapping = {};

  Object.entries(sKAdNetworkSettings[CAMPAIGN_TO_AGENCY_MAPPING]).forEach(([sKAdNetworkId, mapping]) => {
    Object.entries(mapping).forEach(([campaignId, agencyId]) => {
      if (agencyId === vendorId) {
        if (!filteredCampaignToAgencyMapping[sKAdNetworkId]) {
          filteredCampaignToAgencyMapping[sKAdNetworkId] = {};
        }
        filteredCampaignToAgencyMapping[sKAdNetworkId][campaignId] = agencyId;
      }
    });
  });

  return {
    ...sKAdNetworkSettings,
    campaign_to_agency_mapping: filteredCampaignToAgencyMapping,
  };
};

// Used when setting ad partner or location filters in Druid queries
vendorHelpers.VENDOR_DIMENSIONS = {
  APP_ID: 'app_id',
  LAST_ATTRIBUTED_TOUCH_DATA_DOLLAR_3p: 'last_attributed_touch_data_dollar_3p',
  INSTALL_ACTIVITY_TOUCH_DATA_DOLLAR_3p: 'install_activity_touch_data_dollar_3p',
  REENGAGEMENT_ACTIVITY_TOUCH_DATA_DOLLAR_3p: 'reengagement_activity_touch_data_dollar_3p',
  USER_DATA_GEO_COUNTRY_CODE: 'user_data_geo_country_code',
  INSTALL_ACTIVITY_DATA_COUNTRY_CODE: 'install_activity_data_country_code',
  REENGAGEMENT_ACTIVITY_DATA_COUNTRY_CODE: 'reengagement_activity_data_country_code',
  LAST_ATTRIBUTED_TOUCH_DATA_TILDE_AGENCY_ID: 'last_attributed_touch_data_tilde_agency_id',
  INSTALL_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID: 'install_activity_touch_data_tilde_agency_id',
  REENGAGEMENT_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID: 'reengagement_activity_touch_data_tilde_agency_id',
  INSTALL_ACTIVITY_TOUCH_DATA_TILDE_ADVERTISING_PARTNER_NAME: 'install_activity_touch_data_tilde_advertising_partner_name',
  REENGAGEMENT_ACTIVITY_TOUCH_DATA_TILDE_ADVERTISING_PARTNER_NAME:
    'reengagement_activity_touch_data_tilde_advertising_partner_name',
  ANNOTATION_DOLLAR_3P: 'annotation_dollar_3p',
  SK_AD_NETWORK_VALID_MESSAGES: 'skadnetwork-valid-messages',
  BODY_CAMPAIGN_ID: 'body_campaign_id',
  LAST_ATTRIBUTED_TOUCH_DATA_TILDE_CAMPAIGN: 'last_attributed_touch_data_tilde_campaign',
};

vendorHelpers.REVENUE_RELATED_CALCULATED_EVENT_TYPES = [
  'revenue',
  'revenue_in_local_currency',
  'ARPPU',
  'ARPU',
  'LTV',
  'GROSS_PROFIT',
  'ROI',
];

// Removes 'revenue' from aggregations in query if vendor user does not have access to view revenue data
function removeRevenueAggregation(query, degradedPermissionsAccessFilters, datasource) {
  const isSKAdNetwork = vendorHelpers.isTypeOf(vendorHelpers.VENDOR_TYPES.SK_AD_NETWORK, {
    datasource,
  });

  if (
    !degradedPermissionsAccessFilters[REVENUE_DATA] ||
    !query.aggregations ||
    (isSKAdNetwork && degradedPermissionsAccessFilters[AD_PARTNER_TO_CAMPAIGN_ID_MAPPING])
  ) {
    return query;
  }

  let cohortQueryDetected = false;

  if (
    datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.INSTALL_COHORT ||
    datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.REENGAGEMENT_COHORT
  ) {
    cohortQueryDetected = true;
  }

  // If the user is part of an agency, does not have revenue data permissions and
  // manages to select a bookmark in the Cohorts UI that they don't have access to,
  // we'll throw an error
  const shouldBlockCohortReportWithRevData =
    cohortQueryDetected &&
    // check multi measure cohort reports
    (!query.cohort_event_types ||
      query.cohort_event_types.reduce((acc, event) => {
        return acc || vendorHelpers.REVENUE_RELATED_CALCULATED_EVENT_TYPES.indexOf(event) > -1;
      }, false));

  if (shouldBlockCohortReportWithRevData) {
    throw new UnauthorizedError('Access To Revenue Data Restricted');
  }

  return query;
}

function getAdPartnerCampaignIdsMap(adNetworksForApp, agencyId) {
  return Object.keys(adNetworksForApp).reduce((mergedCampaignIdsForAdPartner, adNetworkName) => {
    const campaignToAgencyMapping = adNetworksForApp[adNetworkName][SK_ADNETWORK_SETTINGS][CAMPAIGN_TO_AGENCY_MAPPING];

    if (!campaignToAgencyMapping) {
      return mergedCampaignIdsForAdPartner;
    }

    const campaignToAgencyMapForSKAdNetworks = Object.values(campaignToAgencyMapping);

    const campaignIdsForAgency = getCampaignIdsForAgency(campaignToAgencyMapForSKAdNetworks, agencyId);

    if (!campaignIdsForAgency.length) {
      return mergedCampaignIdsForAdPartner;
    }

    const campaignIdsForAdNetwork = {
      [adNetworkName]: campaignIdsForAgency,
    };

    return Object.assign(mergedCampaignIdsForAdPartner, campaignIdsForAdNetwork);
  }, {});
}

function getCampaignIdsForAgency(campaignToAgencyMap, agencyId) {
  return campaignToAgencyMap.reduce((campaignIdsForAgency, campaignAgencyMap) => {
    const campaignIdsForSKAdNetwork = getCampaignIdsForSKAdNetwork(campaignAgencyMap, agencyId);

    campaignIdsForAgency = [...campaignIdsForAgency, ...campaignIdsForSKAdNetwork];

    return campaignIdsForAgency;
  }, []);
}

function getCampaignIdsForSKAdNetwork(campaignAgencyMap, agencyId) {
  return Object.entries(campaignAgencyMap).reduce((campaignIdsForSKAdNetwork, [campaignId, whitelistedAgencyId]) => {
    if (agencyId === whitelistedAgencyId) {
      campaignIdsForSKAdNetwork.push(campaignId);
    }

    return campaignIdsForSKAdNetwork;
  }, []);
}

// Adds vendor id to filters in query if vendor user is restricted to viewing vendor data only
function addVendorIdToFilter(filter, degradedPermissionsAccessFilters, datasource, organic, isCohortFilterSearch) {
  const isSKAdNetwork = vendorHelpers.isTypeOf(vendorHelpers.VENDOR_TYPES.SK_AD_NETWORK, {
    datasource,
  });

  if (
    !degradedPermissionsAccessFilters[AGENCY_TAGGED_DATA] ||
    (isSKAdNetwork && degradedPermissionsAccessFilters[AD_PARTNER_TO_CAMPAIGN_ID_MAPPING])
  ) {
    return filter;
  }

  if (organic) {
    throw new UnauthorizedError('Access To Data Restricted');
  }
  const permittedVendorId = degradedPermissionsAccessFilters[AGENCY_TAGGED_DATA];

  let dimension = vendorHelpers.VENDOR_DIMENSIONS.LAST_ATTRIBUTED_TOUCH_DATA_TILDE_AGENCY_ID;

  if (
    datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.INSTALL_COHORT ||
    (filter || {})[vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID]
  ) {
    dimension = vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID;
  } else if (
    datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.REENGAGEMENT_COHORT ||
    (filter || {})[vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID]
  ) {
    dimension = vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID;
  }

  let newFilter = {
    type: 'in',
    dimension,
    values: permittedVendorId,
  };

  if (isCohortFilterSearch) {
    newFilter = {
      type: 'or',
      fields: [
        {
          type: 'in',
          dimension: vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID,
          values: permittedVendorId,
        },
        {
          type: 'in',
          dimension: vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_TOUCH_DATA_TILDE_AGENCY_ID,
          values: permittedVendorId,
        },
      ],
    };
  }

  return filter && filter.type
    ? {
        type: 'and',
        fields: [newFilter, filter],
      }
    : newFilter;
}

function addCampaignsToFilter(filter, degradedPermissionsAccessFilters) {
  const permittedCampaignIds = degradedPermissionsAccessFilters[AD_PARTNER_TO_CAMPAIGN_ID_MAPPING];

  if (!permittedCampaignIds) {
    return filter;
  }

  const filters = Object.entries(permittedCampaignIds).map(([partner, campaignIds]) => {
    const campaignFilter = {
      type: 'in',
      dimension: isSANAdPartnerWithNoCampaignIdData(partner)
        ? vendorHelpers.VENDOR_DIMENSIONS.LAST_ATTRIBUTED_TOUCH_DATA_TILDE_CAMPAIGN
        : vendorHelpers.VENDOR_DIMENSIONS.BODY_CAMPAIGN_ID,
      values: campaignIds,
    };

    const partnerFilter = {
      type: 'in',
      dimension: vendorHelpers.VENDOR_DIMENSIONS.ANNOTATION_DOLLAR_3P,
      values: [partner],
    };

    return {
      type: 'and',
      fields: [campaignFilter, partnerFilter],
    };
  });

  const newFilter = {
    type: 'or',
    fields: filters,
  };

  return filter && filter.type
    ? {
        type: 'and',
        fields: [newFilter, filter],
      }
    : newFilter;
}
// Adds country codes to filter in query if vendor user is restricted to viewing data from certain countries only
function addCountryCodesToFilter(filter, degradedPermissionsAccessFilters, datasource, isCohortFilterSearch) {
  const isSKAdNetwork = vendorHelpers.isTypeOf(vendorHelpers.VENDOR_TYPES.SK_AD_NETWORK, {
    datasource,
  });

  if (
    !degradedPermissionsAccessFilters[LOCATION] ||
    (isSKAdNetwork && degradedPermissionsAccessFilters[AD_PARTNER_TO_CAMPAIGN_ID_MAPPING])
  ) {
    return filter;
  }
  const permittedLocations = degradedPermissionsAccessFilters[LOCATION];

  let dimension = vendorHelpers.VENDOR_DIMENSIONS.USER_DATA_GEO_COUNTRY_CODE;

  if (
    datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.INSTALL_COHORT ||
    (filter || {}).install_activity_data_country_code
  ) {
    dimension = vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_DATA_COUNTRY_CODE;
  }
  if (
    datasource === vendorHelpers.VENDOR_DATASOURCES.ARTIFICIAL.REENGAGEMENT_COHORT ||
    (filter || {}).reengagement_activity_data_country_code
  ) {
    dimension = vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_DATA_COUNTRY_CODE;
  }

  let newFilter = {
    type: 'in',
    dimension,
    values: permittedLocations,
  };

  if (isCohortFilterSearch) {
    newFilter = {
      type: 'or',
      fields: [
        {
          type: 'in',
          dimension: vendorHelpers.VENDOR_DIMENSIONS.INSTALL_ACTIVITY_DATA_COUNTRY_CODE,
          values: permittedLocations,
        },
        {
          type: 'in',
          dimension: vendorHelpers.VENDOR_DIMENSIONS.REENGAGEMENT_ACTIVITY_DATA_COUNTRY_CODE,
          values: permittedLocations,
        },
      ],
    };
  }

  return filter && filter.type
    ? {
        type: 'and',
        fields: [newFilter, filter],
      }
    : newFilter;
}

/**
 * Add additional filter for fetching allowed ad network only data
 *   if degradedPermissionsAccessFilters has entry for ad networks
 * @param {DruidFilter} filter
 * @param {Object} degradedPermissionsAccessFilters
 * @param {string} datasource
 * @param {Array} adNetworks
 * @param {boolean} processResponse
 */
function addAdNetworksToFilter(
  filter,
  degradedPermissionsAccessFilters,
  datasource,
  adNetworks,
  organic,
  isCohortFilterSearch
) {
  const isSKAdNetwork = vendorHelpers.isTypeOf(vendorHelpers.VENDOR_TYPES.SK_AD_NETWORK, {
    datasource,
  });

  if (
    !degradedPermissionsAccessFilters[AD_NETWORKS] ||
    (isSKAdNetwork && degradedPermissionsAccessFilters[AD_PARTNER_TO_CAMPAIGN_ID_MAPPING])
  ) {
    return filter;
  }
  if (organic) {
    throw new UnauthorizedError('Access To Data Restricted');
  }

  const permittedMachineNames = degradedPermissionsAccessFilters[AD_NETWORKS];

  if (permittedMachineNames.length === 0) {
    throw new Error(REQUESTING_DATA_FOR_AD_NETWORKS_ERROR);
  }

  const isCohortQuery = vendorHelpers.isTypeOf(vendorHelpers.VENDOR_TYPES.COHORT, {
    filter,
    datasource,
  });

  let adNetworksFilter = {};

  if (isCohortQuery || isCohortFilterSearch) {
    const permittedAdPartners = isCohortFilterSearch
      ? []
      : vendorHelpers.getPermittedAdNetworksAsCompanyNames(permittedMachineNames, adNetworks);

    adNetworksFilter = vendorHelpers.getAdNetworkFiltersForCohorts(
      permittedMachineNames,
      permittedAdPartners,
      datasource,
      filter,
      isCohortFilterSearch
    );
  } else {
    adNetworksFilter = {
      type: 'in',
      dimension: isSKAdNetwork
        ? vendorHelpers.VENDOR_DIMENSIONS.ANNOTATION_DOLLAR_3P
        : vendorHelpers.VENDOR_DIMENSIONS.LAST_ATTRIBUTED_TOUCH_DATA_DOLLAR_3p,
      values: permittedMachineNames,
    };
  }

  return filter && filter.type
    ? {
        type: 'and',
        fields: [adNetworksFilter, filter],
      }
    : adNetworksFilter;
}

vendorHelpers.addAdNetworksToFilter = addAdNetworksToFilter;
vendorHelpers.addCountryCodesToFilter = addCountryCodesToFilter;
vendorHelpers.addVendorIdToFilter = addVendorIdToFilter;
vendorHelpers.addCampaignsToFilter = addCampaignsToFilter;

// FILTER: Clean up filter
vendorHelpers.updateQueryWithVendorPermissions = function (
  query = {},
  degradedPermissionsAccessFilters,
  adNetworks,
  datasource = '',
  organic
) {
  let filter = query.filter || {};
  const cohortFilterSearch = isCohortFilterSearch(query);

  filter = addAdNetworksToFilter(
    filter,
    degradedPermissionsAccessFilters,
    datasource,
    adNetworks,
    organic,
    cohortFilterSearch
  );
  filter = addCountryCodesToFilter(filter, degradedPermissionsAccessFilters, datasource, cohortFilterSearch);
  filter = addVendorIdToFilter(filter, degradedPermissionsAccessFilters, datasource, organic, cohortFilterSearch);
  filter = addCampaignsToFilter(filter, degradedPermissionsAccessFilters);
  query = removeRevenueAggregation(query, degradedPermissionsAccessFilters, datasource);

  return {
    ...query,
    filter,
  };
};

vendorHelpers.applyAccessControlsToWebhook = (luceneQuery, permissions) => {
  let secureWebhookFilter = luceneQuery;
  vendorHelpers.protectedLuceneFields.forEach((accessResource) => {
    if (permissions[accessResource]) {
      secureWebhookFilter = vendorHelpers.getAccessControlledFilterForWebhook(
        secureWebhookFilter,
        permissions[accessResource],
        accessResource
      );
    }
  });
  return secureWebhookFilter;
};

vendorHelpers.applyAccessControlsToPostback = (initialFilter, permissions) => {
  let securePostbackFilter = initialFilter;
  vendorHelpers.protectedLuceneFields.forEach((accessResource) => {
    if (permissions[accessResource]) {
      securePostbackFilter = vendorHelpers.getAccessControlledFilterForPostback(
        securePostbackFilter,
        permissions[accessResource],
        accessResource
      );
    }
  });
  return securePostbackFilter;
};

vendorHelpers.getAccessControlledFilterForPostback = (initialFilter, accessFilter, accessResource) => {
  if (!accessFilter || accessFilter.length === 0) {
    return initialFilter;
  }
  let field, terms;
  const initialFilterCopy = { ...initialFilter };
  switch (accessResource) {
    case AD_NETWORKS:
      // 3P filter is automatically added from the ad-network service
      return initialFilterCopy;
    case AGENCY_TAGGED_DATA:
      field = vendorHelpers.AGENCY_TAGGED_DATA_FIELD;
      terms = accessFilter.length > 0 ? accessFilter[0] : [];
      break;
    case LOCATION:
      field = vendorHelpers.LOCATION_FIELD;
      terms = vendorHelpers.generateFilter(accessFilter, true);
      break;
  }
  if (initialFilterCopy && Object.keys(initialFilterCopy).length > 0) {
    initialFilterCopy[field] = terms;
    return initialFilterCopy;
  }
  return { [field]: terms };
};

vendorHelpers.getAccessControlledFilterForWebhook = (originalQuery, accessFilter, accessResource) => {
  if (!accessFilter || accessFilter.length === 0) {
    return originalQuery;
  }
  let query, terms;

  switch (accessResource) {
    case AD_NETWORKS:
      query = vendorHelpers.AD_NETWORKS_FIELD;
      terms = vendorHelpers.generateFilter(accessFilter);
      break;
    case AGENCY_TAGGED_DATA:
      query = vendorHelpers.AGENCY_TAGGED_DATA_FIELD;
      terms = vendorHelpers.generateFilter(accessFilter);
      break;
    case LOCATION:
      query = vendorHelpers.LOCATION_FIELD;
      terms = vendorHelpers.generateFilter(accessFilter);
      break;
  }
  const originalQueryExists = originalQuery && originalQuery.length > 0;
  const queryAndFilter = `${query}:${terms}`;
  return originalQueryExists ? originalQuery.concat(` AND ${queryAndFilter}`) : queryAndFilter;
};

vendorHelpers.MISSING_FIELD = `Missing required field. Please update filter and try again.`;
vendorHelpers.MISSING_TERMS = `Missing required field's terms. Please update filter and try again.`;
vendorHelpers.INVALID_TERM_FOR_FIELD = `Invalid term for field detected. Please update filter and try again.`;
vendorHelpers.INVALID_OPERATOR = `Invalid operator for required field. Please update filter and try again.`;

const luceneTermError = (field, message) => {
  return {
    field: field,
    message: message,
  };
};

vendorHelpers.deriveField = (flatAst) => {
  if (flatAst.statements[0].ast.field && flatAst.statements[0].ast.field !== '<implicit>') {
    return flatAst.statements[0].ast.field;
  }
  return flatAst.explicitField || '';
};

vendorHelpers.deriveTerms = (flatAst) => {
  const statements = flatAst.statements;
  if (statements.length > 0) {
    return {
      values: statements.map((elem) => elem.ast.term),
      preceding_operator: statements[0].operators.filter((operator) => operator).join(' '),
    };
  }
  return;
};

const getTermsToCheck = (flattenedAST) => {
  if (!flattenedAST) {
    return {};
  }
  const termsToCheck = {};
  flattenedAST.forEach((ast) => {
    const field = vendorHelpers.deriveField(ast);
    const permissionsKey = vendorHelpers.protectedLuceneFieldsToPermissionKeys[field];
    if (permissionsKey) {
      if (!termsToCheck[permissionsKey]) {
        termsToCheck[permissionsKey] = [];
      }
      termsToCheck[permissionsKey].push(vendorHelpers.deriveTerms(ast));
    }
  });
  return termsToCheck;
};

vendorHelpers.permissionsAreEmpty = (permissions) => {
  return !permissions || Object.keys(permissions).length === 0;
};

vendorHelpers.validateLuceneAST = (permissions, flattenedAST) => {
  const termsToCheck = getTermsToCheck(flattenedAST);
  const allowedPrecedingOperators = new Set(['AND']);
  if (vendorHelpers.permissionsAreEmpty(permissions) || !flattenedAST) {
    return [];
  }
  const errors = [];
  [AGENCY_TAGGED_DATA, LOCATION, AD_NETWORKS].forEach((accessResource) => {
    const accessFilterForResource = permissions[accessResource];
    if (!accessFilterForResource || accessFilterForResource.length === 0 || errors.length > 0) {
      return;
    }
    const field = vendorHelpers.permissionKeysToProtectedLuceneFields[accessResource];

    if (Object.keys(termsToCheck).length === 0 || !termsToCheck[accessResource]) {
      errors.push(luceneTermError(field, vendorHelpers.MISSING_FIELD));
    } else if (termsToCheck[accessResource].length === 0) {
      errors.push(luceneTermError(field, vendorHelpers.MISSING_TERMS));
    } else {
      const accessFilterForResourceSet = new Set(accessFilterForResource);
      const termsForAccessResource = termsToCheck[accessResource];
      termsForAccessResource.forEach((terms) => {
        if (errors.length > 0) {
          return;
        }
        if (!allowedPrecedingOperators.has(terms.preceding_operator)) {
          errors.push(luceneTermError(field, vendorHelpers.INVALID_OPERATOR));
          return;
        }
        const values = terms.values;
        const difference = values.filter((term) => !accessFilterForResourceSet.has(term));
        if (difference.length > 0) {
          errors.push(luceneTermError(field, vendorHelpers.INVALID_TERM_FOR_FIELD));
        }
      });
    }
  });
  return errors;
};

vendorHelpers.getAdPartnerCampaignIdsMapForAgency = async (adNetworksForApp, agencyId) => {
  return getAdPartnerCampaignIdsMap(adNetworksForApp, agencyId);
};

module.exports = vendorHelpers;
